/**
 * @fileoverview 订单管理相关API接口
 * @description 包含订单列表查询、创建订单、订单详情等接口
 * @author Vue3 Migration
 * @date 2024
 */

import { request } from '@/utils/http'
import type { PageRequest, PageResponse } from '@/types/api'

// ============================= 接口类型定义 =============================

/**
 * 订单列表查询请求
 * 继承自PageRequest，支持动态查询条件和排序
 */
export interface OrderListRequest extends PageRequest {
  // 继承自PageRequest的字段：
  // current: number    // 当前页码
  // pageSize: number   // 页面大小
  // conditions?: any[] // 动态查询条件
  // sorts?: any[]      // 排序条件
}

/**
 * 订单创建请求
 */
export interface OrderCreateRequest {
  /** 订单类型：1-需要报价 2-已有报价 3-代订舱 */
  orderType: number
  /** 企业ID */
  enterpriseId?: string
  /** 订单数据（根据订单类型不同，数据结构不同） */
  orderData?: BookingDataRequest | InquiryDataRequest | QuoteDataRequest
  /** 订舱页面VO数据 */
  bookingVO?: import('./types').BookingPageVO
  /** 报价单号 */
  quotationNo?: string
  /** 特价申请单ID；特价下单时必填 */
  specialPriceApplicationId?: string
}

/**
 * 统一下单创建请求（CCTOWER-2170）
 */
export interface UnifiedOrderCreateRequest {
  businessType: string
  fields: Record<string, unknown>
  submitAction?: string
  enterpriseId?: number | string
  orderId?: number | string
  idempotencyKey?: string
  templateId?: string
  templateVersion?: number
}

/**
 * 统一下单创建响应（CCTOWER-2170）
 */
export interface UnifiedOrderCreateResponse {
  orderId?: number | string
  orderNo?: string
  orderStatus?: number
  businessType?: string
}

/**
 * 统一订单更新请求（CCTOWER-2170 扩展）
 */
export interface UnifiedOrderUpdateRequest {
  orderId: number | string
  businessType: string
  fields: Record<string, unknown>
  templateId?: string
  templateVersion?: number
}

/**
 * 订舱数据请求（代订舱类型使用）
 */
export interface BookingDataRequest {
  /** 船名 */
  vesselName: string
  /** 承运人 */
  carrier: string
  /** 航次 */
  voyageNumber: string
  /** 起运港 */
  pol: string
  /** 目的港 */
  pod: string
  /** 预计离港时间 */
  etd: string
  /** 预计到港时间 */
  eta: string
  /** 截关时间 */
  cutoffTime: string
  /** 运输模式：FCL/LCL */
  transportMode: string
  /** 集装箱数量 */
  containerCount?: number
  /** 货物总重量(KGS) */
  grossWeight?: number
  /** 货物总体积(CBM) */
  volume?: number
  /** 货物总件数 */
  pieces?: number
  /** 运费 */
  freightCharges?: number
  /** 币种 */
  currency?: string
}

/**
 * 询价数据请求（需要报价类型使用）
 */
export interface InquiryDataRequest {
  /** 起运港 */
  pol: string
  /** 目的港 */
  pod: string
  /** 运输模式：FCL/LCL */
  transportMode: string
  /** 货物类型 */
  cargoType?: string
  /** 货物重量(KGS) */
  grossWeight?: number
  /** 货物体积(CBM) */
  volume?: number
  /** 货物件数 */
  pieces?: number
  /** 预计装货时间 */
  estimatedLoadingDate?: string
  /** 预计交货时间 */
  estimatedDeliveryDate?: string
  /** 特殊要求 */
  specialRequirements?: string
}

/**
 * 报价数据请求（已有报价类型使用）
 */
export interface QuoteDataRequest {
  /** 报价编号 */
  quotationNo: string
  /** 其他报价相关数据 */
  [key: string]: any
}

/**
 * 订单详情请求
 */
export interface OrderDetailRequest {
  /** 订单ID */
  id: string
}

/**
 * 上传订单文件请求
 */
export interface UploadOrderFileParams {
  /** 订单ID */
  orderId: string | number
  /** 业务文件类型 */
  fileType: string
  /** 文件 */
  file: File
  /** 是否客户可见 */
  customerVisible?: boolean
}

/**
 * 运输委托单文件删除请求
 */
export interface DeleteTransportConsignmentFileParams {
  /** 委托单订单ID */
  orderId: string | number
  /** 订单文件记录ID */
  fileRecordId: string | number
}

/**
 * 运输委托单文件客户可见状态更新请求
 */
export interface UpdateTransportConsignmentFileVisibilityParams {
  /** 委托单订单ID */
  orderId: string | number
  /** 订单文件记录ID */
  fileRecordId: string | number
  /** 是否客户可见 */
  customerVisible: boolean
}

/**
 * 订单工作流节点VO
 */
export interface OrderWorkflowNodeVO {
  /** 节点ID */
  nodeId: string
  /** 节点名称 */
  nodeName: string
  /** 节点状态 */
  nodeStatus: number
  /** 完成时间 */
  completionTime?: string
  /** 任务状态：1-未完成 2-部分完成 3-已完成 */
  taskStatus: number
  /** 子任务列表 */
  tasks?: OrderWorkflowTaskVO[]
  /** 节点图标 */
  nodeIcon?: string
}

/**
 * 订单工作流任务VO
 */
export interface OrderWorkflowTaskVO {
  /** 任务ID */
  taskId: string
  /** 任务名称 */
  taskName: string
  /** 任务状态：1-未开始 2-进行中 3-已完成 4-已跳过 5-已暂停 */
  taskStatus: number
  /** 结束时间 */
  endTime?: string
  /** 任务类型 1 运营任务 2客户任务 */
  taskType?: number
}

/**
 * 订单列表VO
 */
export interface OrderListVO {
  /** 订单ID */
  orderId: string
  /** 订单编号 */
  orderNo: string
  /** 运输类型 */
  transportMode: string
  /** 起运港 */
  pol: string
  /** 卸货港 */
  pod: string
  /** 目的港 */
  pdl?: string
  /** 船公司 */
  carrier: string
  /** 船名 */
  vesselName: string
  /** 航次 */
  voyageNumber?: string
  /** 创建人 */
  createdBy: string
  /** 创建时间 */
  dateCreated: string
  /** 更新人 */
  modifiedBy: string
  /** 更新时间 */
  dateModified: string
  /** 订单状态 */
  orderStatus: number
  /** 订单类型 */
  orderType: number
  /** 优先级 */
  priority: number
  /** 业务节点时间轴（已废弃，改用 businessStatus） */
  workflowNodes: OrderWorkflowNodeVO[]
  /** 集装箱数量 */
  containerCount: number
  /** 货物总重量(KGS) */
  grossWeight: number
  /** 货物总体积(CBM) */
  volume: number
  /** 货物总件数 */
  pieces: number
  /** 运费 */
  freight: number
  /** Cargo业务编号 */
  cargoJobNo?: string
  /** 业务参考号 */
  customReferenceNo?: string
  /** MBL单号 */
  mblNo?: string
  /** HBL单号 */
  hblNo?: string
  /** 箱号（多个箱号用逗号分隔） */
  containerNos?: string
  /** 预计离港时间 */
  etd?: string
  /** 预计到港时间 */
  eta?: string
  /** 航线代码 */
  laneCode?: string
  /** 码头 */
  terminal?: string
  /** 截关时间 */
  cutoffTime?: string
  /** 预计航程（天） */
  voyageDays?: number
  /** 运输条款 */
  shippingTerms?: string
  /** 订舱号 */
  bookingNo?: string
  /** AMS No */
  amsNo?: string
  /** ISF No */
  isfNo?: string
  /** 中转港1 */
  pot1?: string
  /** 中转港2 */
  pot2?: string
  /** 箱型箱量详情 */
  containerDetails?: string
  /** 外部系统关联ID */
  externalId: string
  /** 外部系统代码（如 cargoware，非空表示外部系统同步而来） */
  externalSource?: string
  /** 订单同步状态：0-未同步 1-同步中 2-已同步 3-同步失败 */
  syncStatus?: number
  /** 业务参考号 */
  referenceNo?: string
  /** 来源编号 */
  sourceCode?: string
  /** 订单来源（如 API_SYNC_CARGOWARE=API同步(CargoWare)、DIRECT_CREATE=直接创建） */
  orderSource?: string
  /** 业务状态（SOP 状态）- 从 CW 获取 */
  businessStatus?: OrderBusinessStatusVO
  /** 视图模板业务类型编码，如 SEA_FCL、AIR（CCTOWER-2172） */
  businessType?: string
  /** 业务类型展示名（CCTOWER-2172） */
  businessTypeLabel?: string
  /** 业务字段 fieldId -> value（CCTOWER-2172） */
  businessFields?: Record<string, unknown>
}

/**
 * 订单任务VO
 */
export interface OrderTaskVO {
  /** 任务执行记录ID */
  taskId: string
  /** 任务状态：1-未开始 2-进行中 3-已完成 4-已跳过 5-已暂停 */
  taskStatus: number
  /** 开始时间 */
  startTime?: string
  /** 结束时间 */
  endTime?: string
  /** 计划结束时间 */
  plannedEndTime?: string
  /** 指派用户ID */
  assignedUserId?: string
  /** 执行用户ID */
  executorUserId?: string
  /** 执行时长（分钟） */
  duration?: number
  /** 执行结果 */
  executionResult?: string
  /** 执行备注 */
  executionRemarks?: string
  /** 跳过原因 */
  skipReason?: string
  /** 创建时间 */
  dateCreated: string
  /** 节点ID */
  nodeId: string
  /** 任务编码 */
  taskCode: string
  /** 任务名称 */
  taskName: string
  /** 任务描述 */
  taskDescription: string
  /** 任务类型 */
  taskType: number
  /** 任务实例ID */
  taskInstanceId?: string
  /** 页面代码 - 用于判断跳转到哪个页面 */
  pageCode: string
  /** 任务数据 */
  dataJson: any
}

/**
 * 订单消息VO
 */
export interface OrderMessageVO {
  /** 消息ID */
  messageId: string
  /** 消息内容 */
  content: string
  /** 发送人 */
  sender: string
  /** 发送时间 */
  sendTime: string
  /** 附件列表 */
  attachments?: string[]
}

/**
 * 订单货物信息VO
 */
export interface OrderCargoInfoVO {
  /** 货物编号 */
  cargoNo: string
  /** 货物名称 */
  cargoName: string
  /** 货物类型 */
  cargoType: string
  /** 件数 */
  quantity: number
  /** 单位 */
  unit: string
  /** 毛重(KGS) */
  grossWeight: number
  /** 净重(KGS) */
  netWeight: number
  /** 体积(CBM) */
  volume: number
  /** 货物分类 */
  cargoCategory: string
  /** 是否危险品 */
  dangerousGoods: boolean
  /** 危险品等级 */
  dangerousClass?: string
  /** 货物价值 */
  cargoValue?: number
  /** 货物价值币种 */
  cargoCurrency?: string
  /** 货物备注 */
  cargoRemarks?: string
}

/**
 * 订单集装箱信息VO
 */
export interface OrderContainerInfoVO {
  /** 集装箱号 */
  containerNo: string
  /** 封条号 */
  sealNo: string
  /** 箱型 */
  containerType: string
  /** 箱尺寸 */
  containerSize: string
  /** 皮重(KGS) */
  tareWeight: number
  /** 最大载重(KGS) */
  maxPayload: number
  /** 集装箱状态 */
  containerStatus: string
  /** SOC标识：0-COC承运人箱 1-SOC货主箱 */
  socFlag?: number
  /** 提箱地点 */
  pickupLocation?: string
  /** 还箱地点 */
  returnLocation?: string
  /** 提箱日期 */
  pickupDate?: string
  /** 还箱日期 */
  returnDate?: string
  /** 集装箱备注 */
  containerRemarks?: string
}

/**
 * 订单参考编号VO
 */
export interface OrderReferenceNumbersVO {
  /** 订舱号 */
  bookingNo?: string
  /** MBL单号 */
  mblNo?: string
  /** HBL单号 */
  hblNo?: string
  /** AMS No */
  amsNo?: string
  /** ISF No */
  isfNo?: string
  /** 自定义编号 */
  customNo?: string
  /** 业务参考号 */
  referenceNo?: string
}

/**
 * 订单关联方信息VO
 */
export interface OrderRelatedPartyVO {
  /** 关联方类型 */
  partyType: string
  /** 关联方名称 */
  partyName: string
  /** 联系人 */
  contactPerson?: string
  /** 地址 */
  address?: string
  /** 电话 */
  phone?: string
  /** 邮箱 */
  email?: string
}

/**
 * 订单概览信息VO
 */
export interface OrderOverviewVO {
  /** 业务类型（如 FCL, LCL, AIR 等） */
  businessType?: string
  /** 订单编号 */
  orderNo: string
  /** 订单类型：1-海运订单 2-空运订单 3-陆运订单 4-铁路订单 */
  orderType: number
  /** 订单状态：1-草稿 2-进行中 3-已完成 4-已取消 */
  orderStatus: number
  /** 关联工作流ID */
  workflowId?: string
  /** 工作流实例ID */
  workflowInstanceId?: string
  /** 优先级：1-高 2-中 3-低 */
  priority: number
  /** 预计完成时间 */
  estimatedCompletionDate?: string
  /** 实际完成时间 */
  actualCompletionDate?: string
  /** 备注 */
  remarks?: string
  /** 创建时间 */
  dateCreated: string
  /** 创建人 */
  createdBy?: string
  /** 船名 */
  vesselName?: string
  /** 承运人 */
  carrier?: string
  /** 码头 */
  terminal?: string
  /** 截关时间 */
  cutoffTime?: string
  /** 预计离港时间 */
  etd?: string
  /** 预计到港时间 */
  eta?: string
  /** 航次 */
  voyageNumber?: string
  /** 航线代码 */
  serviceCode?: string
  /** 航线代码（后端字段名） */
  laneCode?: string
  /** 进港代码 */
  portCallCode?: string
  /** 预计航程（天） */
  transitTime?: number
  /** 预计航程（天，后端字段名） */
  voyageDays?: number
  /** 运输条款 */
  shippingTerms?: string
  /** 运输模式：FCL/LCL */
  transportMode?: string
  /** 起运港 */
  pol?: string
  /** 卸货港 */
  pod?: string
  /** 目的港 */
  pdl?: string
  /** 中转港1 */
  pot1?: string
  /** 中转港2 */
  pot2?: string
  /** 集装箱数量 */
  containerCount?: number
  /** 货物总重量(KGS) */
  grossWeight?: number
  /** 货物总体积(CBM) */
  volume?: number
  /** 货物总件数 */
  pieces?: number
  /** 运费 */
  freightCharges?: number
  /** 币种 */
  currency?: string
  /** 货物信息 */
  cargoInfoList?: OrderCargoInfoVO[]
  /** 集装箱信息 */
  containerInfoList?: OrderContainerInfoVO[]
  /** 参考编号 */
  referenceNumbers?: OrderReferenceNumbersVO
  /** 关联方信息 */
  relatedPartyList?: OrderRelatedPartyVO[]
  /** 用户ID */
  userId?: string
  /** 企业ID */
  enterpriseId?: string
  /** 自定义字段1 */
  userDefinedField1?: string
  /** 自定义字段2 */
  userDefinedField2?: string
}

/**
 * 订单文件VO
 */
export interface OrderFileVO {
  /** 订单文件记录ID */
  id?: string | number
  /** 文件ID */
  fileId: string
  /** 订单ID */
  orderId?: string
  /** 文件名 */
  fileName: string
  /** 显示文件名 */
  fileNameDisplay?: string
  /** 文件路径 */
  filePath: string
  /** 文件访问URL */
  fileUrl?: string
  /** 文件大小 */
  fileSize: number
  /** 上传人 */
  uploadedBy?: string
  /** 上传用户ID */
  uploadUserId?: string
  /** 上传时间 */
  uploadTime: string
  /** 业务文件类型 */
  fileType: string
  /** 文件扩展名 */
  fileExtension?: string
  /** 文件来源 */
  source?: string
  /** 文控文档ID（文控导入时用于下载兜底） */
  docControlDocumentId?: string | number
  /** 创建时间 */
  dateCreated?: string
  /** CW 文件类型编码 */
  documentType?: string
  /** CW 文件类型中文名 */
  documentTypeName?: string
  /** 是否来自 CargoWare 查询 */
  fromCw?: boolean
}

/**
 * 订单协作方联系信息VO
 */
export interface OrderCollaboratorVO {
  /** 协作方ID */
  collaboratorId: string
  /** 角色 */
  role: string
  /** 姓名 */
  name: string
  /** 公司名称 */
  company: string
  /** 电话 */
  phone?: string
  /** 邮箱 */
  email?: string
}

/**
 * 视图模板消费扩展（CCTOWER-2171 / 2166）
 */
export interface UnifiedOrderVO {
  businessType?: string
  businessTypeLabel?: string
  businessFields?: Record<string, unknown>
}

/**
 * 订单详情VO
 */
export interface OrderDetailVO {
  /** 订单ID */
  orderId: string
  /** 未完成任务列表 */
  unfinishedTasks: OrderTaskVO[]
  /** 已完成任务列表 */
  completedTasks: OrderTaskVO[]
  /** 业务节点时间轴（已废弃，改用 businessStatus） */
  workflowNodes: OrderWorkflowNodeVO[]
  /** 运踪节点时间轴（预留字段） */
  shippingNodes?: OrderWorkflowNodeVO[]
  /** 留言板信息 */
  messages: OrderMessageVO[]
  /** 概览信息 */
  overview: OrderOverviewVO
  /** 文件列表 */
  files: OrderFileVO[]
  /** 协作方联系信息 */
  collaborators: OrderCollaboratorVO[]
  /** 应收费用列表 */
  receivableFees?: OrderFeeVO[]
  /** 应付费用列表 */
  payableFees?: OrderFeeVO[]
  /** 订单同步状态：0-未同步 1-同步中 2-已同步 3-同步失败 */
  syncStatus?: number
  /** CargoWare 业务号 */
  cargowareJobNo?: string
  /** 运踪信息 */
  trackingInfo?: OrderTrackingNodeVO
  /** 业务状态（SOP 状态）- 从 CW 获取 */
  businessStatus?: OrderBusinessStatusVO
  /** 视图模板消费扩展（CCTOWER-2166） */
  unifiedOrder?: UnifiedOrderVO
}

/**
 * 订单业务状态（SOP 状态）VO
 */
export interface OrderBusinessStatusVO {
  /** 节点数量 */
  count: number
  /** 结果状态 */
  result: string
  /** 节点列表（CW 返回的原始结构，前端按需渲染） */
  data: any[]
}

/**
 * 订单业务状态查询请求
 */
export interface OrderBusinessStatusQueryRequest {
  /** X 项目订单 ID（唯一入参，使用字符串避免精度丢失） */
  orderId: string
}

// ============================= 运踪相关类型定义 =============================

/**
 * 运踪节点信息VO（来自运踪配置系统）
 */
export interface TrackingNodeVO {
  /** 节点ID */
  id: number
  /** 渠道ID */
  channelId: number
  /** 业务ID */
  businessId: number
  /** 节点编码 */
  code: string
  /** 节点中文名称 */
  nameCn: string
  /** 节点英文名称 */
  nameEn: string
  /** 启用状态：0-禁用，1-启用 */
  enable: number
  /** 是否外部展示：0-否，1-是 */
  externalDisplay: number
  /** 文件ID（节点图标） */
  fileId?: string
  /** 排序号 */
  sort: number
}

/**
 * 箱货信息VO
 */
export interface ContainerUsageVO {
  /** 码头 */
  portDistrict?: string
  /** 船名 */
  vessel?: string
  /** 航次 */
  voyageFlight?: string
  /** 箱号 */
  containerNo?: string
  /** 持箱人 */
  containerCarrier?: string
  /** 箱型 */
  eqSize?: string
  /** 装货港 */
  loadingPort?: string
  /** 箱种 */
  eqType?: string
  /** 卸货港 */
  dischargingPort?: string
  /** 箱状态（仅提供上海港口） */
  containerStatus?: string
  /** 目的港 */
  destinationPort?: string
  /** 铅封号 */
  sealNo?: string
  /** 进场时间 */
  approachDate?: string
  /** 海关放行 */
  customsClearance?: string
  /** 进场方式 */
  enteringMode?: string
  /** 海放说明 */
  customsReleaseInfo?: string
  /** 出场时间 */
  playDate?: string
  /** 收到海放时间 */
  customReleaseDate?: string
  /** 出场方式 */
  appearanceType?: string
  /** 码头放行 */
  dockRelease?: string
  /** 码头配载 */
  wharfStowage?: string
}

/**
 * 码头配载信息VO（实际运踪数据）
 */
export interface WharfStowageVO {
  /** 提空箱时间 */
  emptyPickUpTime?: string
  /** 提空箱分子数量 */
  emptyPickUpTimeNum?: number
  /** 开港时间/预计开港时间 */
  terminalOpendate?: string
  /** 截单日期/港区截单 */
  filecutoff?: string
  /** 进场时间/重箱进场分子数量 */
  approachDateNum?: number
  /** 海关放行分子数量 */
  customReleaseDateNum?: number
  /** 码头放行分子数量 */
  wharfReleaseDateNum?: number
  /** 实际靠泊时间 */
  actualBerthPlanDate?: string
  /** 码头配载分子数量 */
  wharfStowageDateNum?: number
  /** 实际离泊时间/实际开船 */
  actualUnberthPlanDate?: string
  /** 卸船时间 */
  portDischargeTime?: string
  /** 卸船分子数量 */
  portDischargeTimeNum?: number
  /** 实际提重时间/提重 */
  actualPickUpTime?: string
  /** 实际提重时间/提重分子数量 */
  actualPickUpTimeNum?: number
  /** 目的港 实际还空时间/还箱 */
  destPortReturnTime?: string
  /** 目的港 实际还空时间/还箱分子数量 */
  destPortReturnTimeNum?: number
}

/**
 * 空运数据追踪信息VO（实际运踪数据，来源：CargoWare dc_air_tracking）
 */
export interface AirTrackingVO {
  /** 订舱时间 */
  bookingDate?: string
  /** 实际进场时间 */
  actualEntryTime?: string
  /** 实际配载时间 */
  actualLoadingTime?: string
  /** 实际起飞时间 */
  actualDepartureTime?: string
  /** 实际抵达时间 */
  actualArrivalTime?: string
  /** 实际收单时间 */
  actualReceivingTime?: string
  /** 实际卸货时间 */
  actualUnloadingTime?: string
  /** 实际到货通知时间 */
  actualArrivalNotificationTime?: string
  /** 实际提货时间 */
  actualPickupTime?: string
}

/**
 * 实际运踪信息VO（来自Cargo系统）
 */
export interface CargoTrackingVO {
  /** 业务编号 */
  jobNo?: string
  /** 起运港五字码 */
  loadingPort?: string
  /** 目的港五字码 */
  dischargingPort?: string
  /** 总箱子数量 */
  totalContainer?: number
  /** 箱货信息列表 */
  containerUsages?: ContainerUsageVO[]
  /** 数据追踪页签Icon信息（海运出口） */
  dataTrackingIconVO?: WharfStowageVO
  /** 业务类型码：SE=海运出口、SI=海运进口、AE=空运出口、AI=空运进口 */
  lob?: string
  /** 空运数据追踪信息（lob=AE 时返回） */
  airTracking?: AirTrackingVO
}

/**
 * 订单运踪信息VO（完整运踪信息）
 */
export interface OrderTrackingNodeVO {
  /** 固定节点信息列表（从运踪配置系统查询） */
  fixedNodes: TrackingNodeVO[]
  /** 订单实际运踪信息（从Cargo系统查询） */
  actualTracking?: CargoTrackingVO
}

/**
 * 订单运踪查询请求
 */
export interface OrderTrackingQueryRequest {
  /** 外部系统ID（jobUuid），对应订单的externalId字段 */
  externalId: string
  /** 订单类型：1-海运订单 2-空运订单 3-陆运订单 4-铁路订单，不传按海运处理 */
  orderType?: number
}

// ============================= API接口定义 =============================

/**
 * 分页查询订单列表
 * @param params 查询参数
 * @returns 分页订单列表
 */
export const getOrderPage = (params: OrderListRequest): Promise<PageResponse<OrderListVO>> => {
  return request.post('/booking/order/page', params)
}

/**
 * 创建订单
 * @param params 创建订单参数
 * @returns 订单ID
 */
export const createOrder = (params: OrderCreateRequest): Promise<string> => {
  return request.post('/booking/order/create', params)
}

/**
 * 统一下单（表单引擎，CCTOWER-2170）
 */
export const createUnifiedOrder = (params: UnifiedOrderCreateRequest): Promise<UnifiedOrderCreateResponse> => {
  return request.post('/booking/order/unified/create', params)
}

/**
 * 统一订单更新（表单引擎，CCTOWER-2170）
 */
export const updateUnifiedOrder = (params: UnifiedOrderUpdateRequest): Promise<UnifiedOrderCreateResponse> => {
  return request.post('/booking/order/unified/update', params)
}

/**
 * 获取订单详情
 * @param params 订单详情查询参数
 * @returns 订单详情
 */
export const getOrderDetail = (params: OrderDetailRequest): Promise<OrderDetailVO> => {
  return request.post('/booking/order/detail', params)
}

/**
 * 上传订单文件
 * @param params 上传文件参数
 * @returns 订单文件信息
 */
export const uploadOrderFile = (params: UploadOrderFileParams): Promise<OrderFileVO> => {
  const formData = new FormData()
  formData.append('orderId', String(params.orderId))
  formData.append('fileType', params.fileType)
  formData.append('file', params.file)
  formData.append('customerVisible', String(Boolean(params.customerVisible)))

  // 不要手动设置 Content-Type，让浏览器为 FormData 自动附带 boundary
  return request.post('/booking/order/file/upload', formData)
}

/**
 * 逻辑删除运输委托单文件
 * @param params 委托单订单ID与文件记录ID
 * @returns 是否删除成功
 */
export const deleteTransportConsignmentFile = (
  params: DeleteTransportConsignmentFileParams
): Promise<boolean> => {
  return request.post('/booking/order/file/delete', params)
}

/**
 * 更新运输委托单文件客户可见状态
 * @param params 委托单订单ID、文件记录ID和客户可见状态
 * @returns 是否更新成功
 */
export const updateTransportConsignmentFileVisibility = (
  params: UpdateTransportConsignmentFileVisibilityParams
): Promise<boolean> => {
  return request.post('/booking/order/file/customer-visible', params)
}

/**
 * 查询订单运踪信息
 * @param params 运踪查询参数
 * @returns 运踪信息
 * @description 根据外部系统ID（jobUuid）查询订单的运踪信息，包括固定节点信息和实际运踪数据
 */
export const getOrderTracking = (params: OrderTrackingQueryRequest): Promise<OrderTrackingNodeVO> => {
  return request.post('/booking/order/tracking', params)
}

/**
 * 订单运踪批量查询请求
 */
export interface OrderTrackingBatchQueryRequest {
  /** 查询类型：1-单证号（HBL NO. / MBL NO. / 业务参考号 / Cargoware业务号 / 订单编号） 2-箱号 */
  queryType: number
  /** 查询号码列表（前端已按分隔符切分去重，最多20个） */
  numbers: string[]
}

/**
 * 订单运踪批量查询结果
 */
export interface OrderTrackingBatchQueryVO {
  /** 命中的订单列表（已按订单去重） */
  matchedOrders: OrderListVO[]
  /** 未匹配到订单的查询号码（原样返回） */
  unmatchedNumbers: string[]
}

/**
 * 运踪批量查询（单证号/箱号，一次请求支持多个号码）
 * @param params 批量查询参数
 * @returns 命中的订单列表与未匹配号码
 */
export const batchQueryTracking = (
  params: OrderTrackingBatchQueryRequest
): Promise<OrderTrackingBatchQueryVO> => {
  return request.post('/booking/order/tracking/batch-query', params)
}

/**
 * 查询订单业务状态（SOP 状态）
 * @param params 查询参数（orderId / jobNo / jobUuid 三选一）
 * @returns 业务状态信息
 */
export const getOrderBusinessStatus = (params: OrderBusinessStatusQueryRequest): Promise<OrderBusinessStatusVO> => {
  return request.post('/booking/order/business-status', params)
}

export interface OrderCwFileListVO {
  files?: OrderFileVO[]
  fileLocked?: boolean
}

export interface CwDocumentTypeOption {
  code: string
  name: string
}

export const listCwOrderFiles = (params: { orderId: string | number }): Promise<OrderCwFileListVO> => {
  return request.post('/booking/order/file/cw-list', params)
}

export const downloadCwOrderFile = (params: { orderId: string | number; fileId: string }): Promise<Blob> => {
  return request.get('/booking/order/file/cw-download', params, {
    responseType: 'blob'
  })
}

export const removeOrderFile = (params: { orderId: string | number; fileRecordId: string | number }): Promise<boolean> => {
  return request.post('/booking/order/file/remove', params)
}

export const listCwDocumentTypes = (lob?: string): Promise<CwDocumentTypeOption[]> => {
  const query = lob ? `?lob=${encodeURIComponent(lob)}` : ''
  return request.post(`/booking/order/file/cw-document-types${query}`, {})
}

/**
 * 更新订单详情
 * @param params 订单详情对象
 * @returns 操作结果
 */
export const updateOrderDetail = (params: OrderDetailVO): Promise<boolean> => {
  return request.post('/booking/order/update', params)
}

/**
 * 删除订单
 * @returns 订单详情
 * @param id
 * @param config
 */
export const deleteOrder = (id: string, config?: any): Promise<boolean> => {
  return request.post('/booking/order/delete', { id }, config)
}

// ============================= 任务管理API =============================

/**
 * 任务列表查询请求
 * 继承自PageRequest，支持动态查询条件和排序
 */
export interface TaskListRequest extends PageRequest {
  // 继承自PageRequest的字段：
  // pageNum: number       // 页码，最小为1
  // pageSize: number      // 分页大小，范围 1-1000
  // sort?: string         // 排序字段，格式：字段名 排序方式
  // paging?: boolean      // 是否分页，默认true
  // conditions?: any[]    // 动态查询条件列表
  // sorts?: any[]         // 动态排序条件列表
}

/**
 * 任务列表VO
 */
export interface TaskListVO {
  /** 任务实例ID */
  taskInstanceId: string
  /** 订单ID */
  orderId: string
  /** 工作流实例ID */
  workflowInstanceId: string
  /** 节点ID */
  nodeId: string
  /** 节点名称 */
  nodeName: string
  /** 任务ID */
  taskId: string
  /** 任务编码 */
  taskCode: string
  /** 任务名称 */
  taskName: string
  /** 任务描述 */
  taskDescription: string
  /** 任务性质：1-外部任务 2-内部任务 */
  taskType: number
  /** 对接人类型：1-专属对接人 2-业务负责人 */
  contactPersonType: number
  /** 专属对接人：1-专属销售 2-专属客服 3-专属操作 4-专属单证 5-专属商务 6-专属财务 */
  exclusiveContactPerson: number
  /** 执行模式：1-并行 2-串行 */
  executionMode: number
  /** 任务状态：1-未开始 2-进行中 3-已完成 4-已跳过 */
  taskStatus: number
  /** 任务状态名称 */
  taskStatusName: string
  /** 指派用户ID */
  assignedUserId: string
  /** 指派用户名称 */
  assignedUserName: string
  /** 执行用户ID */
  executorUserId: string
  /** 执行用户名称 */
  executorUserName: string
  /** 计划开始时间 */
  plannedStartTime: string
  /** 计划结束时间 */
  plannedEndTime: string
  /** 实际开始时间 */
  actualStartTime?: string
  /** 实际结束时间 */
  actualEndTime?: string
  /** 订单编号 */
  orderNo: string
  /** 执行时长（分钟） */
  duration?: number
  /** 执行结果 */
  executionResult?: string
  /** 执行备注 */
  executionRemarks?: string
  /** 跳过原因 */
  skipReason?: string
  /** 任务数据JSON格式 */
  dataJson?: any
  /** 是否可以跳过 */
  skippable: boolean
  /** 页面配置ID */
  pageConfigId: string
  /** 创建时间 */
  dateCreated: string
  /** 更新时间 */
  dateModified: string
}

/**
 * 任务跳过请求参数
 */
export interface TaskSkipRequest {
  /** 任务状态ID */
  taskInstanceId: string
  /** 跳过原因 */
  skipReason: string
}

/**
 * 任务处理请求参数
 */
export interface TaskProcessRequest {
  /** 任务状态ID */
  taskInstanceId: string
  /** 执行备注 */
  executionRemarks?: string
  /** 任务数据 */
  taskData?: any
}

/**
 * 分页查询任务列表
 * @param params 查询参数
 * @returns 分页任务列表
 */
export const getTaskList = (params: TaskListRequest): Promise<PageResponse<TaskListVO>> => {
  return request.post('/booking/task/list', params)
}

/**
 * 获取任务详情
 * @param taskInstanceId 任务实例ID
 * @returns 任务详情
 */
export const getTaskDetail = (taskInstanceId: string): Promise<any> => {
  return request.post('/booking/task/detail', { id: taskInstanceId })
}

/**
 * 保存
 * @param params 任务实例ID
 * @returns 任务详情
 */
export const saveTaskDraft = (params: TaskProcessRequest): Promise<boolean> => {
  return request.post('/booking/task/draft', params)
}

/**
 * 跳过任务
 * @param params 跳过任务参数
 * @returns 操作结果
 */
export const skipTask = (params: TaskSkipRequest): Promise<boolean> => {
  return request.post('/booking/task/skip', params)
}

/**
 * 处理任务（完成任务）
 * @param params 处理任务参数
 * @returns 操作结果
 */
export const processTask = (params: TaskProcessRequest): Promise<boolean> => {
  return request.post('/booking/task/process', params)
}

// ============================= 订单费用管理API =============================

/**
 * 订单费用VO
 */
export interface OrderFeeVO {
  /** 费用ID */
  id?: string
  /** 订单ID */
  orderId: number | string
  /** 费用归属单据类型 */
  billType?: number | null
  /** 费用类型：1-应收费用 2-应付费用 */
  feeType: number
  /** 结算对象ID */
  settlementObjectId?: number | string
  /** 结算对象 */
  settlementObject: string
  /** 费用名称 */
  feeName: string
  /** 费用ID（用于匹配费用基础数据） */
  feeId?: string
  /** 费用归属/运输段落：1-港前 2-干线 3-尾程 4-全程 */
  feeSegment?: number | null
  /** 国内国外：1-国内 2-国外 */
  domesticForeign?: number | null
  /** 单价 */
  unitPrice: number
  /** 数量 */
  quantity: number
  /** 币种 */
  currency: string
  /** 汇率 */
  exchangeRate?: number
  /** 税率(%) */
  taxRate: number
  /** 税额 */
  taxAmount: number
  /** 含税价 */
  inclusiveTaxPrice: number
  /** 不含税价 */
  exclusiveTaxPrice: number
  /** 预估金额（含税） */
  estimatedInclusiveTaxPrice?: number
  /** 预估金额（不含税） */
  estimatedExclusiveTaxPrice?: number
  /** 开票抬头 */
  invoiceTitle?: string
  /** 备注 */
  remarks?: string
  /** 计费单位ID */
  measureId?: string
  /** 计费单位名称 */
  measureName?: string
  /** 本位币币种 */
  baseCurrency?: string
  /** 折合本位币金额 */
  baseCurrencyAmount?: number
  /** 核销状态：0-未核销 1-已核销 2-部分核销 */
  writeOffStatus?: number | null
  /** 账单号 */
  billNo?: string
  /** 对方账单号 */
  counterpartyBillNo?: string
  /** 核销金额 */
  writeOffAmount?: number
  /** 未核销金额 */
  unwrittenOffAmount?: number
  /** 核销日期 */
  writeOffDate?: string
  /** 收付日期 */
  receiptPaymentDate?: string
  /** 对账状态：0-未对账 1-已对账 */
  reconciliationStatus?: number | null
  /** 发票类型 */
  invoiceType?: string
  /** 开票日期 */
  invoiceDate?: string
  /** 发票号码 */
  invoiceNo?: string
  /** 费用预估状态：0-未推送 1-已推送 */
  estimatePushStatus?: number | null
  /** 费用结算状态：0-未推送 1-已推送 */
  settlementPushStatus?: number | null
  /** 所属运单 */
  waybillNo?: string
  /** NS预提单号 */
  nsAccrualNo?: string
  /** 费用来源单据ID */
  sourceOrderId?: number | string | null
  /** 费用来源单据号 */
  sourceOrderNo?: string | null
  /** 费用来源行ID */
  sourceFeeId?: number | string | null
  /** 是否参与结算：Y-参与结算 N-不参与结算 */
  settlementFlag?: string
  /** 费用分摊批次号 */
  allocationBatchNo?: string | null
  /** 费用分摊依据 */
  allocationBasis?: string | null
  /** 费用分摊依据值 */
  allocationBasisValue?: number | null
  /** 费用分摊依据合计 */
  allocationBasisTotal?: number | null
  /** 费用分摊比例 */
  allocationRatio?: number | null
  /** 创建人ID */
  createdBy?: number
  /** 创建时间 */
  createTime?: string
  /** 修改人ID */
  modifiedBy?: number
  /** 修改时间 */
  modifyTime?: string
}

/**
 * 订单费用请求
 */
export interface OrderFeeRequest {
  /** 费用ID（更新时必填） */
  id?: string
  /** 订单ID */
  orderId: number | string
  /** 费用归属单据类型 */
  billType?: number | null
  /** 费用类型：1-应收费用 2-应付费用 */
  feeType: number
  /** 结算对象ID */
  settlementObjectId?: number | string
  /** 结算对象 */
  settlementObject: string
  /** 费用名称 */
  feeName: string
  /** 费用ID（用于匹配费用基础数据） */
  feeId?: string
  /** 费用归属/运输段落：1-港前 2-干线 3-尾程 4-全程 */
  feeSegment?: number | null
  /** 国内国外：1-国内 2-国外 */
  domesticForeign?: number | null
  /** 单价 */
  unitPrice: number
  /** 数量 */
  quantity?: number
  /** 币种 */
  currency?: string
  /** 汇率 */
  exchangeRate?: number
  /** 税率(%) */
  taxRate?: number
  /** 税额 */
  taxAmount?: number
  /** 含税价 */
  inclusiveTaxPrice: number
  /** 不含税价 */
  exclusiveTaxPrice: number
  /** 预估金额（含税） */
  estimatedInclusiveTaxPrice?: number
  /** 预估金额（不含税） */
  estimatedExclusiveTaxPrice?: number
  /** 开票抬头 */
  invoiceTitle?: string
  /** 备注 */
  remarks?: string
  /** 计费单位ID */
  measureId?: string
  /** 计费单位名称 */
  measureName?: string
  /** 本位币币种 */
  baseCurrency?: string
  /** 折合本位币金额 */
  baseCurrencyAmount?: number
  /** 核销状态：0-未核销 1-已核销 2-部分核销 */
  writeOffStatus?: number | null
  /** 账单号 */
  billNo?: string
  /** 对方账单号 */
  counterpartyBillNo?: string
  /** 核销金额 */
  writeOffAmount?: number
  /** 未核销金额 */
  unwrittenOffAmount?: number
  /** 核销日期 */
  writeOffDate?: string
  /** 收付日期 */
  receiptPaymentDate?: string
  /** 对账状态：0-未对账 1-已对账 */
  reconciliationStatus?: number | null
  /** 发票类型 */
  invoiceType?: string
  /** 开票日期 */
  invoiceDate?: string
  /** 发票号码 */
  invoiceNo?: string
  /** 费用预估状态：0-未推送 1-已推送 */
  estimatePushStatus?: number | null
  /** 费用结算状态：0-未推送 1-已推送 */
  settlementPushStatus?: number | null
  /** 所属运单 */
  waybillNo?: string
  /** NS预提单号 */
  nsAccrualNo?: string
  /** 费用来源单据ID */
  sourceOrderId?: number | string | null
  /** 费用来源单据号 */
  sourceOrderNo?: string | null
  /** 费用来源行ID */
  sourceFeeId?: number | string | null
  /** 是否参与结算：Y-参与结算 N-不参与结算 */
  settlementFlag?: string
  /** 费用分摊批次号 */
  allocationBatchNo?: string | null
  /** 费用分摊依据 */
  allocationBasis?: string | null
  /** 费用分摊依据值 */
  allocationBasisValue?: number | null
  /** 费用分摊依据合计 */
  allocationBasisTotal?: number | null
  /** 费用分摊比例 */
  allocationRatio?: number | null
}

/**
 * 订单费用列表查询请求
 */
export interface OrderFeeListRequest extends PageRequest {
  /** 订单ID */
  orderId?: number | string
  /** 费用归属单据类型 */
  billType?: number
  /** 费用类型：1-应收费用 2-应付费用 */
  feeType?: number
  /** 结算对象（模糊查询） */
  settlementObject?: string
  /** 费用名称（模糊查询） */
  feeName?: string
}

/**
 * 创建费用
 * @param params 费用请求
 * @returns 费用ID
 */
export const createOrderFee = (params: OrderFeeRequest): Promise<string> => {
  return request.post('/booking/order/fee/create', params)
}

/**
 * 更新费用
 * @param params 费用请求
 * @returns 操作结果
 */
export const updateOrderFee = (params: OrderFeeRequest): Promise<boolean> => {
  return request.post('/booking/order/fee/update', params)
}

/**
 * 删除费用
 * @param params 主键请求
 * @returns 操作结果
 */
export const deleteOrderFee = (params: { id: number }): Promise<boolean> => {
  return request.post('/booking/order/fee/delete', params)
}

/**
 * 根据ID查询费用
 * @param params 主键请求
 * @returns 费用VO
 */
export const getOrderFeeById = (params: { id: number }): Promise<OrderFeeVO> => {
  return request.post('/booking/order/fee/detail', params)
}

/**
 * 分页查询费用列表
 * @param params 查询请求
 * @returns 费用分页列表
 */
export const pageOrderFees = (params: OrderFeeListRequest): Promise<PageResponse<OrderFeeVO>> => {
  return request.post('/booking/order/fee/page', params)
}

/**
 * 根据订单ID和费用类型查询费用列表
 * @param params 查询请求
 * @returns 费用列表
 */
export const listOrderFeesByOrder = (params: OrderFeeListRequest): Promise<OrderFeeVO[]> => {
  return request.post('/booking/order/fee/listByOrder', params)
}

/**
 * 批量创建费用
 * @param params 费用请求列表
 * @returns 操作结果
 */
export const batchCreateOrderFees = (params: OrderFeeRequest[]): Promise<boolean> => {
  return request.post('/booking/order/fee/batchCreate', params)
}

/**
 * 批量删除费用
 * @param params 费用ID列表
 * @returns 操作结果
 */
export const batchDeleteOrderFees = (params: number[]): Promise<boolean> => {
  return request.post('/booking/order/fee/batchDelete', params)
}

// ============================= 枚举定义 =============================

/**
 * 订单类型枚举
 */
export enum OrderType {
  /** 需要报价 */
  NEED_QUOTE = 1,
  /** 已有报价 */
  HAS_QUOTE = 2,
  /** 代订舱 */
  BOOKING = 3
}

/**
 * 订单状态枚举
 */
export enum OrderStatus {
  /** 草稿 */
  DRAFT = 1,
  /** 进行中 */
  IN_PROGRESS = 2,
  /** 已完成 */
  COMPLETED = 3,
  /** 已取消 */
  CANCELLED = 4
}

/**
 * 订单来源枚举
 */
export enum OrderSourceEnum {
  /** 运价查询创建 (Price Query) */
  PRICE_QUERY = 'PRICE_QUERY',
  /** 询价单创建 (Inquiry) */
  INQUIRY = 'INQUIRY',
  /** 报价单创建 (Quotation) */
  QUOTATION = 'QUOTATION',
  /** 创建订舱 (Booking) */
  BOOKING = 'BOOKING',
  /** API同步(CargoWare) (API Sync CargoWare) */
  API_SYNC_CARGOWARE = 'API_SYNC_CARGOWARE'
}

/**
 * 任务状态枚举
 */
export enum TaskStatus {
  /** 未开始 */
  NOT_STARTED = 1,
  /** 进行中 */
  IN_PROGRESS = 2,
  /** 已完成 */
  COMPLETED = 3,
  /** 已跳过 */
  SKIPPED = 4,
  /** 已暂停 */
  PAUSED = 5
}

/**
 * 优先级枚举
 */
export enum Priority {
  /** 高 */
  HIGH = 1,
  /** 中 */
  MEDIUM = 2,
  /** 低 */
  LOW = 3
}

/**
 * 通过关联单号查询订单业务关联请求
 */
export interface OrderIdByBusinessRelationRequest {
  /** 业务类型：inquiry-询价, quotation-报价, booking-订舱 */
  businessType: string
  /** 业务编码（关联单号） */
  businessCode: string
}

/**
 * 订单业务关联VO
 */
export interface OrderBusinessRelationVO {
  /** 订单ID */
  orderId: number
  /** 订单编号 */
  orderNo?: string
  /** 业务类型：inquiry/quote/booking等 */
  businessType: string
  /** 业务实体ID */
  businessId: number
  /** 业务编码 */
  businessCode: string
  /** 关联类型：1-创建来源 2-关联业务 3-引用业务 */
  relationType: number
  /** 关联描述 */
  relationDescription?: string
}

/**
 * 通过关联单号查询订单业务关联
 * @param params 查询参数
 * @returns 订单业务关联列表
 */
export const getOrderBusinessRelation = (params: OrderIdByBusinessRelationRequest): Promise<OrderBusinessRelationVO[]> => {
  return request.post('/booking/order/getOrderBusinessRelation', params)
}

/**
 * 手动触发订单同步到外部系统
 * @param orderIds 订单ID列表
 * @returns 操作结果
 */
export const triggerOrderSync = (orderIds: string[]): Promise<boolean> => {
  return request.post('/booking/order/trigger-sync', orderIds)
}
